import { useEffect, useRef } from 'react';
import type { ChangeEvent } from 'react';
import { useTranslation } from '@/i18n';
import { cx } from '@/lib/cx';
import styles from './SchedulePage.module.css';

interface TextFilterProps {
  /** The filter currently in force — that is, what is in the URL. */
  value: string;
  onChange: (value: string) => void;
  /**
   * Required, not optional: the clear button hides itself with
   * `:placeholder-shown`, which only matches while a placeholder exists.
   */
  placeholder: string;
  ariaLabel: string;
  /** Uppercases as you type — for IATA codes and flight numbers. */
  upper?: boolean;
}

/**
 * A text filter in a column header, with a clear inside the field.
 *
 * It commits after a pause rather than on every keystroke, because the committed
 * value goes into the URL and fires a request: writing per character would mean a
 * query per character.
 *
 * **Uncontrolled, deliberately.** The obvious build — a `useState` draft plus an
 * effect syncing it from the prop — sets state during the commit on every external
 * change, and React's own guidance is against it. A controlled input keyed off the
 * URL is worse still: each commit rewrites the URL, which would re-render and reset
 * the field the reader is still typing in.
 *
 * So the DOM node holds what is being typed, and an effect nudges it back into line
 * with the URL only while the reader is NOT in this field — which is exactly when a
 * Back, a shared link or "Clear all" should be allowed to change it.
 */
export const TextFilter = ({
  value,
  onChange,
  placeholder,
  ariaLabel,
  upper = false,
}: TextFilterProps) => {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);
  const timer = useRef<number | undefined>(undefined);

  useEffect(() => {
    const input = inputRef.current;
    if (!input) return;

    // Never yank the text out from under someone mid-word.
    if (document.activeElement === input) return;
    if (input.value !== value) input.value = value;
  }, [value]);

  // A filter that is still waiting to commit when the reader navigates away should
  // not fire afterwards.
  useEffect(() => () => window.clearTimeout(timer.current), []);

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const input = event.currentTarget;

    if (upper) {
      // Uppercase in place, restoring the caret: without this, correcting the middle
      // of `KC101` sends the cursor to the end after every character.
      const caret = input.selectionStart;
      input.value = input.value.toUpperCase();
      if (caret !== null) input.setSelectionRange(caret, caret);
    }

    const next = input.value;
    window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => onChange(next), 350);
  };

  const clear = () => {
    const input = inputRef.current;

    // Drop a keystroke that has not committed yet, or it would land after the clear
    // and put the text straight back.
    window.clearTimeout(timer.current);

    if (input) {
      input.value = '';
      // Focus goes to the field, not nowhere: clearing a filter is usually the start
      // of typing a different one.
      input.focus();
    }

    onChange('');
  };

  return (
    <span className={styles.filterCell}>
      <input
        ref={inputRef}
        type="text"
        className={cx(styles.filterInput, value !== '' && styles.filterActive)}
        defaultValue={value}
        onChange={handleChange}
        placeholder={placeholder}
        aria-label={ariaLabel}
        spellCheck={false}
        autoComplete="off"
      />

      {/*
        Shown only when the field has text, and by CSS rather than by state: the input
        is uncontrolled, so a boolean for this would have to be kept in step with the
        DOM node from three places. `:placeholder-shown` already knows, and it knows a
        keystroke ahead of the debounce — which is what makes the button feel attached
        to the field rather than to the request.

        `visibility: hidden` also keeps it out of the tab order while it is invisible,
        so an empty filter row is still four tab stops rather than eight.
      */}
      <button
        type="button"
        className={styles.filterClear}
        onClick={clear}
        aria-label={t('schedule.clearFilter', { name: ariaLabel })}
      >
        ×
      </button>
    </span>
  );
};
